import { showNotification } from '../../utils.js';
import CompanyService from '../../services/company.js';
import { WhatsAppPreview } from './components/WhatsAppPreview.js';

const translations = {
  pageTitle: 'Шаблоны сообщений',
  backButton: '&#8656; Назад к компаниям',
  existingTemplates: 'Существующие шаблоны',
  tableHeaders: {
    orderState: 'Статус заказа',
    deliveryType: 'Тип доставки',
    templateText: 'Текст шаблона',
    status: 'Статус',
    actions: 'Действия',
  },
  form: {
    title: 'Добавить/Редактировать шаблон',
    placeholder: 'Введите текст шаблона...',
    orderState: 'Статус заказа',
    deliveryType: 'Тип доставки',
    save: 'Сохранить',
  },
  preview: 'Предпросмотр сообщения',
  notifications: {
    statusUpdated: 'Статус шаблона успешно обновлен',
    deleted: 'Шаблон успешно удален',
    toggleError: 'Не удалось изменить статус шаблона',
    deleteError: 'Не удалось удалить шаблон',
    error: 'Произошла ошибка',
    loadError: 'Не удалось загрузить данные компании',
    resetSuccess: 'Шаблоны сброшены',
  },
  confirmDelete: 'Вы уверены, что хотите удалить этот шаблон?',
  buttons: {
    edit: 'Редактировать',
    delete: 'Удалить',
    reset: 'Сбросить шаблоны',
  },
};

// Delivery options with descriptions and corresponding API values
const deliveryOptions = [
  {
    label: 'по городу — Kaspi Доставка или силами продавца',
    value: 'DELIVERY_LOCAL',
    isKaspiDelivery: null,
  },
  {
    label: 'доставка в Kaspi Postomat',
    value: 'DELIVERY_PICKUP',
    isKaspiDelivery: true,
  },
  {
    label: 'самовывоз',
    value: 'DELIVERY_PICKUP',
    isKaspiDelivery: false,
  },
  {
    label: 'Kaspi Доставка (по регионам)',
    value: 'DELIVERY_REGIONAL_TODOOR',
    isKaspiDelivery: true,
  },
  {
    label: 'доставка по области до склада с зоной выдачи заказов, покупатель заберет самовывозом',
    value: 'DELIVERY_REGIONAL_PICKUP',
    isKaspiDelivery: null,
  },
];

export default class TemplatesView {
  constructor() {
    // Cache main container element and initialize properties.
    this.mainContent = document.getElementById('main-content');
    this.company = null;
    // Keep bound handlers so we can de‑duplicate listeners across re-renders
    this._boundClickHandler = null;
    this._boundChangeHandler = null;
    // These elements will be set after rendering.
    this.templateForm = null;
    this.orderStateSelect = null;
    this.deliveryTypeSelect = null;
    this.textArea = null;
    this.editingTemplateIdInput = null;
    this.previewContainer = null;
    this.deliveryTypeContainer = null;
  }

  /**
   * Truncate text and add a title tooltip with full content.
   * @param {string} text - The text to truncate.
   * @param {number} maxLength - Maximum length allowed.
   * @returns {string} - HTML string.
   */
  truncateText(text, maxLength) {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return `<span title="${text}">${text.substring(0, maxLength)}...</span>`;
  }

  /**
   * Load the matching template from the company.templates array based on the current form selections.
   */
  loadTemplate() {
    const state = this.orderStateSelect.value;
    const selectedOption = this.deliveryTypeSelect.selectedOptions[0]?.dataset;

    const template = this.company.templates.find((t) => {
      if (state === 'ARCHIVE') {
        return t.order_state === 'ARCHIVE';
      } else {
        // Convert the dataset value to proper boolean/null
        const isKaspiDelivery =
          selectedOption?.isKaspiDelivery === 'true'
            ? true
            : selectedOption?.isKaspiDelivery === 'false'
            ? false
            : null;
        return (
          t.order_state === 'KASPI_DELIVERY' &&
          t.delivery_type === selectedOption?.value &&
          t.is_kaspi_delivery === isKaspiDelivery
        );
      }
    });

    if (template) {
      this.textArea.value = template.text;
    } else {
      this.textArea.value = '';
    }
    this._updatePreview();
    this.editingTemplateIdInput.value = '';
  }

  /**
   * Mount the view by loading company data and rendering the view.
   * @param {number|string} companyId 
   */
  async mount(companyId) {
    try {
      this.company = await CompanyService.getCompany(companyId);
      this.render();
    } catch (error) {
      showNotification(translations.notifications.loadError, 'error');
      console.error('Error in TemplatesView:', error);
    }
  }

  /**
   * Render the entire view markup and cache references to important elements.
   */
  render() {
    const markup = `
      <div class="container mx-auto px-4 py-6">
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-2xl font-bold text-gray-900">${translations.pageTitle} - ${this.company.name}</h2>
          <div class="flex items-center gap-3">
            <button id="reset-templates" class="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded">
              ${translations.buttons.reset}
            </button>
            <button id="back-to-companies" class="px-4 py-2 text-gray-600 hover:text-gray-800">
              ${translations.backButton}
            </button>
          </div>
        </div>

        <div class="grid grid-cols-12 gap-4 items-start">
          <div class="col-span-12 bg-white rounded-lg shadow p-6 mb-4">
            <h4 class="text-lg font-medium text-gray-700 mb-4">${translations.existingTemplates}</h4>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">${translations.tableHeaders.orderState}</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">${translations.tableHeaders.deliveryType}</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">${translations.tableHeaders.templateText}</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">${translations.tableHeaders.status}</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">${translations.tableHeaders.actions}</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  ${this.company.templates
                    .map((template) => `
                      <tr>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ${template.order_state === 'ARCHIVE' ? 'Заказ доставлен' : 'Новый заказ'}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ${
                            template.order_state === 'ARCHIVE'
                              ? '-'
                              : this.truncateText(
                                  this.getDeliveryTypeLabel(template.delivery_type, template.is_kaspi_delivery),
                                  15
                                )
                          }
                        </td>
                        <td class="px-6 py-4 text-sm text-gray-900">
                          <div class="max-w-md truncate">${template.text}</div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <label class="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" class="sr-only peer template-toggle" 
                                   data-template-id="${template.id}"
                                   ${template.active ? 'checked' : ''}>
                            <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer 
                                 peer-checked:after:translate-x-full peer-checked:after:border-white 
                                 after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                                 after:bg-white after:border-gray-300 after:border after:rounded-full 
                                 after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div class="flex gap-4">
                            <button class="text-blue-600 hover:text-blue-800 edit-template" data-template-id="${template.id}" title="${translations.buttons.edit}">
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                              </svg>
                            </button>
                            <button class="text-red-600 hover:text-red-800 delete-template" data-template-id="${template.id}" title="${translations.buttons.delete}">
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    `)
                    .join('')}
                </tbody>
              </table>
            </div>
          </div>

          <div class="col-span-6 bg-white rounded-lg shadow p-6">
            <h4 class="text-lg font-medium text-gray-700 mb-4">${translations.form.title}</h4>
            <form id="template-form" class="space-y-4">
              <input type="hidden" name="active" value="true">
              <textarea name="text" required rows="6"
                        class="bg-gray-50 p-2 text-sm block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        placeholder="${translations.form.placeholder}"></textarea>
              <div class="space-y-4">
                <div>
                  <label for="order_state" class="block text-sm font-medium text-gray-700">${translations.form.orderState}</label>
                  <select name="order_state" required
                          class="bg-gray-50 p-2 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
                    <option value="KASPI_DELIVERY">Новый заказ</option>
                    <option value="ARCHIVE">Заказ доставлен</option>
                  </select>
                </div>
                <div id="delivery-type-container">
                  <label for="delivery_type" class="block text-sm font-medium text-gray-700">${translations.form.deliveryType}</label>
                  <select name="delivery_type" required
                          class="bg-gray-50 p-2 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
                    <!-- Options will be populated dynamically -->
                  </select>
                </div>
              </div>
              <input type="hidden" name="editingTemplateId" value="">
              <button type="submit"
                      class="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">
                ${translations.form.save}
              </button>
            </form>
          </div>

          <div class="col-span-6 bg-white rounded-lg shadow p-6">
            <h4 class="text-lg font-medium text-gray-700 mb-4">${translations.preview}</h4>
            <div id="preview-container"></div>
          </div>
        </div>
      </div>
    `;

    this.mainContent.innerHTML = markup;
    this._cacheElements();
    this.populateDeliveryTypes();
    this._handleOrderStateChange(); // set proper visibility/validation
    this.loadTemplate(); // load matching template text (if any)
    this._attachEventListeners();
    this._updatePreview(); // initial preview render
  }

  /**
   * Cache DOM element references after render.
   */
  _cacheElements() {
    this.templateForm = document.getElementById('template-form');
    this.orderStateSelect = this.templateForm.querySelector('[name="order_state"]');
    this.deliveryTypeSelect = this.templateForm.querySelector('[name="delivery_type"]');
    this.textArea = this.templateForm.querySelector('[name="text"]');
    this.editingTemplateIdInput = this.templateForm.querySelector('input[name="editingTemplateId"]');
    this.previewContainer = document.getElementById('preview-container');
    this.deliveryTypeContainer = this.templateForm.querySelector('#delivery-type-container');
  }

  /**
   * Get a human-friendly label for a given delivery type and its isKaspiDelivery flag.
   * @param {string} type 
   * @param {boolean|null} isKaspiDelivery 
   * @returns {string}
   */
  getDeliveryTypeLabel(type, isKaspiDelivery) {
    const option = deliveryOptions.find(
      (opt) =>
        opt.value === type &&
        (opt.isKaspiDelivery === isKaspiDelivery ||
          (opt.isKaspiDelivery === null && isKaspiDelivery === null))
    );
    return option ? option.label : type;
  }

  /**
   * Populate the delivery type select element with options.
   */
  populateDeliveryTypes() {
    this.deliveryTypeSelect.innerHTML = ''; // Clear previous options

    deliveryOptions.forEach((option) => {
      const optionElement = document.createElement('option');
      optionElement.textContent = option.label;
      optionElement.dataset.value = option.value;
      optionElement.dataset.isKaspiDelivery = option.isKaspiDelivery;
      this.deliveryTypeSelect.appendChild(optionElement);
    });
  }

  /**
   * Attach all event listeners using delegation where appropriate.
   */
  _attachEventListeners() {
    // Remove previous delegated listeners if they exist to avoid duplicates
    if (this._boundClickHandler) {
      this.mainContent.removeEventListener('click', this._boundClickHandler);
    }
    if (this._boundChangeHandler) {
      this.mainContent.removeEventListener('change', this._boundChangeHandler);
    }

    // Use event delegation on mainContent for click events.
    this._boundClickHandler = (e) => {
      const toggleEl = e.target.closest('.template-toggle');
      if (toggleEl) this._handleToggleChange(e, toggleEl);

      const editBtn = e.target.closest('.edit-template');
      if (editBtn) this._handleEditTemplate(e, editBtn);

      const deleteBtn = e.target.closest('.delete-template');
      if (deleteBtn) this._handleDeleteTemplate(e, deleteBtn);

      const backBtn = e.target.closest('#back-to-companies');
      if (backBtn) this._handleBackButtonClick(e);

      const resetBtn = e.target.closest('#reset-templates');
      if (resetBtn) this._handleResetTemplates(e);
    };

    // Listen for changes on select elements (delegation for change events)
    this._boundChangeHandler = (e) => {
      if (e.target === this.orderStateSelect) {
        this._handleOrderStateChange();
        if (this.orderStateSelect.value === 'KASPI_DELIVERY') {
          this.populateDeliveryTypes();
        }
        this.loadTemplate();
      }
      if (e.target === this.deliveryTypeSelect) {
        this.loadTemplate();
      }
    };

    this.mainContent.addEventListener('click', this._boundClickHandler);
    this.mainContent.addEventListener('change', this._boundChangeHandler);

    // Update preview when the textarea content changes.
    this.textArea.addEventListener('input', () => this._updatePreview());

    // Listen for form submission.
    this.templateForm.addEventListener('submit', (e) => this._handleFormSubmit(e));
  }

  /**
   * Update the preview container with the WhatsApp preview of the text.
   */
  _updatePreview() {
    this.previewContainer.innerHTML = WhatsAppPreview(this.textArea.value);
  }

  /**
   * Show or hide the delivery type container based on the order state.
   */
  _handleOrderStateChange() {
    const orderState = this.orderStateSelect.value;
    if (orderState === 'ARCHIVE') {
      this.deliveryTypeContainer.style.display = 'none';
      this.deliveryTypeSelect.removeAttribute('required');
    } else {
      this.deliveryTypeContainer.style.display = 'block';
      this.deliveryTypeSelect.setAttribute('required', 'required');
    }
  }

  /**
   * Handle toggling a template’s active status.
   */
  async _handleToggleChange(e, toggleEl) {
    const templateId = toggleEl.dataset.templateId;
    try {
      await CompanyService.toggleTemplate(this.company.id, templateId);
      this.company = await CompanyService.getCompany(this.company.id);
      showNotification(translations.notifications.statusUpdated);
    } catch (error) {
      console.error('Failed to toggle template:', error);
      showNotification(error.message || translations.notifications.toggleError, 'error');
      // Revert checkbox state on error
      toggleEl.checked = !toggleEl.checked;
    }
  }

  /**
   * Handle editing a template – populate the form with existing data.
   */
  _handleEditTemplate(e, editBtn) {
    const templateId = editBtn.dataset.templateId;
    const template = this.company.templates.find((t) => t.id === parseInt(templateId, 10));
    if (!template) return;

    this.textArea.value = template.text;
    this.orderStateSelect.value = template.order_state;
    if (template.order_state !== 'ARCHIVE') {
      this.populateDeliveryTypes();
      // Select the matching delivery option
      const matchingOption = Array.from(this.deliveryTypeSelect.options).find((opt) => {
        return (
          opt.dataset.value === template.delivery_type &&
          (opt.dataset.isKaspiDelivery === String(template.is_kaspi_delivery) ||
            (opt.dataset.isKaspiDelivery === 'null' && template.is_kaspi_delivery === null))
        );
      });
      if (matchingOption) {
        matchingOption.selected = true;
      }
    }
    this.editingTemplateIdInput.value = template.id;
    // Update the preview with the current template text.
    this._updatePreview();
    this._handleOrderStateChange();
    this.templateForm.scrollIntoView({ behavior: 'smooth' });
  }

  /**
   * Handle deleting a template.
   */
  async _handleDeleteTemplate(e, deleteBtn) {
    if (!confirm(translations.confirmDelete)) return;
    const templateId = deleteBtn.dataset.templateId;
    try {
      await CompanyService.deleteTemplate(this.company.id, templateId);
      showNotification(translations.notifications.deleted);
      // Reload company data and re-render the view.
      this.company = await CompanyService.getCompany(this.company.id);
      this.render();
    } catch (error) {
      console.error('Failed to delete template:', error);
      showNotification(error.message || translations.notifications.deleteError, 'error');
    }
  }

  /**
   * Handle the back button click.
   */
  _handleBackButtonClick(e) {
    window.location.hash = '#/companies';
  }

  /**
   * Reset templates to defaults from YAML.
   */
  async _handleResetTemplates(e) {
    if (!confirm('Вы уверены, что хотите сбросить шаблоны до значений по умолчанию?')) return;
    try {
      await CompanyService.resetTemplates(this.company.id);
      this.company = await CompanyService.getCompany(this.company.id);
      this.render();
      showNotification(translations.notifications.resetSuccess);
    } catch (error) {
      console.error('Failed to reset templates:', error);
      showNotification(error.message || translations.notifications.error, 'error');
    }
  }

  /**
   * Handle form submission for creating or updating a template.
   */
  async _handleFormSubmit(e) {
    e.preventDefault();
    const formData = new FormData(this.templateForm);
    const orderState = formData.get('order_state');
    const editingTemplateId = formData.get('editingTemplateId');

    const selectedOption = this.deliveryTypeSelect.selectedOptions[0];
    const deliveryType = selectedOption?.dataset.value;
    const isKaspiDelivery =
      selectedOption?.dataset.isKaspiDelivery === 'true'
        ? true
        : selectedOption?.dataset.isKaspiDelivery === 'false'
        ? false
        : null;

    const templateData = {
      text: formData.get('text'),
      active: formData.get('active') === 'true',
      order_state: orderState,
      delivery_type: orderState === 'ARCHIVE' ? null : deliveryType,
      is_kaspi_delivery: orderState === 'ARCHIVE' ? null : isKaspiDelivery,
    };

    try {
      if (editingTemplateId) {
        await CompanyService.updateTemplate(this.company.id, parseInt(editingTemplateId, 10), templateData);
        showNotification(translations.notifications.statusUpdated);
      } else {
        await CompanyService.createTemplate(this.company.id, templateData);
        showNotification(translations.notifications.statusUpdated);
      }
      // Refresh company data and re-render the view.
      this.company = await CompanyService.getCompany(this.company.id);
      this.render();
    } catch (error) {
      console.error('Error:', error);
      showNotification(error.message || translations.notifications.error, 'error');
    }
  }
}
